<template>
  <div class="e4s-flex-column">
    <div class="e4s-flex-column e4s-gap--standard">
      <div class="e4s-subheader--general">
        Enter a partial or the first few characters of an athlete's first or last name
      </div>

      <div class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center">
        <div class="e4s-flex-row e4s-justify-flex-row-vert-center e4s-flex-grow">
          <InputDebounce
            class="e4s-square--right e4s-flex-grow"
            :class="searchTerm.length >= 3 ? 'e4s-input-field--highlight' : ''"
            placeholder="Search athlete..."
            :value="searchTerm"
            @input="onSearchTermChanged"
          />
          <ButtonGenericV2
            class="e4s-button--auto"
            with-input="right"
            @click="onSearchTermClear"
            :disabled="searchTerm.length === 0"
            text="X"
          />
        </div>
      </div>
    </div>

    <div class="e4s-vertical-spacer--standard"></div>

    <!-- Empty State -->
    <div v-if="!hasSearched" class="e4s-info-message">
      Start typing to search for athletes in this competition.
    </div>

    <!-- No Results State -->
    <div v-else-if="hasSearched && filteredEntries.length === 0" class="e4s-info-message">
      No athletes found matching "{{ searchTerm }}".
    </div>

    <!-- Results -->
    <div v-else>
      <div class="e4s-flex-row e4s-gap--standard e4s-flex-row--end e4s-subheader--general">
        <span>Showing {{ displayedEntries.length }} of {{ filteredEntries.length }} matches</span>
      </div>

      <div class="e4s-vertical-spacer--standard"></div>

      <div class="e4s-flex-row">
        <div class="e4s-flex-row--end e4s-header--500">Estimated</div>
      </div>
      <div class="e4s-vertical-spacer--standard"></div>
      <hr class="dat-e4s-hr-only dat-e4s-hr--light" />

      <AthleteEntryRow
        v-for="item in displayedEntries"
        :key="item.entry.entryId"
        :entry="item.entry"
        :event-name="item.eventName"
        :is-track="item.isTrack"
        :is-wait-list-closed="isWaitListClosed"
      />

      <!-- Load More Button -->
      <div
        v-if="displayedEntries.length < filteredEntries.length"
        class="e4s-flex-row e4s-justify-flex-row-center"
      >
        <div class="e4s-vertical-spacer--standard"></div>
        <ButtonGenericV2 button-type="primary" text="Load More" @onButtonClicked="loadMore" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import type {
  StoreCompResultsState,
  IndividualEntry,
  CompEvent
} from '@/components/results/store-comp-results'
import * as ResultsService from '@/components/results/results-service'
import InputDebounce from '@/common/ui/fields/input-debounce.vue'
import ButtonGenericV2 from '@/common/ui/buttons/button-generic-v2.vue'
import AthleteEntryRow from './AthleteEntryRow.vue'

const props = defineProps({
  storeCompResultsState: {
    type: Object as PropType<StoreCompResultsState>,
    required: true
  }
})

const searchTerm = ref('')
const hasSearched = ref(false)
const pageSize = 50
const currentPage = ref(1)

function onSearchTermChanged(value: string) {
  searchTerm.value = value
  if (searchTerm.value.length >= 1) {
    hasSearched.value = true
    currentPage.value = 1 // Reset pagination on new search
  } else {
    hasSearched.value = false
  }
}

function onSearchTermClear() {
  searchTerm.value = ''
  hasSearched.value = false
  currentPage.value = 1
}

function loadMore() {
  currentPage.value++
}

interface AthleteEntryDisplay {
  entry: IndividualEntry
  eventName: string
  isTrack: boolean
  isWaitListClosed: boolean
}

// computed property to determine if wait list is closed based on competition options
const isWaitListClosed = computed(() => {
  const isWaitClosed = ResultsService.isWaitingListCloseed(
    props.storeCompResultsState.competitionOnTheDay.options
  )
  return isWaitClosed
})

// Aggregate all entries across all events in the competition
const allCompetitionEntries = computed<AthleteEntryDisplay[]>(() => {
  const entries: AthleteEntryDisplay[] = []
  const events = props.storeCompResultsState.competitionOnTheDay.events

  for (const egId in events) {
    const compEvent = events[egId] as CompEvent
    if (compEvent.entries) {
      const isTrack = ResultsService.isTrackEvent(compEvent)
      for (const athleteId in compEvent.entries) {
        entries.push({
          entry: compEvent.entries[athleteId],
          eventName: compEvent.name,
          isTrack,
          isWaitListClosed: isWaitListClosed.value
        })
      }
    }
  }

  return entries
})

// Filter entries by first or last name and sort to cluster athlete events together
const filteredEntries = computed<AthleteEntryDisplay[]>(() => {
  if (!hasSearched.value || searchTerm.value.length < 0) {
    return []
  }

  // Split search term into words (e.g., "Daniel Of" -> ["daniel", "of"])
  const searchWords = searchTerm.value
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0)

  const filtered = allCompetitionEntries.value.filter((item) => {
    const firstName = (item.entry.firstName || '').toLowerCase()
    const surName = (item.entry.surName || '').toLowerCase()
    const fullName = `${firstName} ${surName}`

    // Every word in the search term must be found somewhere in the full name
    return searchWords.every((word) => fullName.includes(word))
  })

  return filtered.sort((a, b) => {
    // Sort by first name
    const firstNameCompare = (a.entry.firstName || '').localeCompare(b.entry.firstName || '')
    if (firstNameCompare !== 0) return firstNameCompare

    // Then by surname
    const surNameCompare = (a.entry.surName || '').localeCompare(b.entry.surName || '')
    if (surNameCompare !== 0) return surNameCompare

    // Then by event name to order events for the same athlete
    return (a.eventName || '').localeCompare(b.eventName || '')
  })
})

// Paginated entries to display
const displayedEntries = computed<AthleteEntryDisplay[]>(() => {
  const start = 0
  const end = currentPage.value * pageSize
  return filteredEntries.value.slice(start, end)
})
</script>

<style scoped>
.e4s-justify-flex-row-center {
  justify-content: center;
}
</style>
